/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Grid,
} from "@mui/material";
import {
  formatBoolean,
  //formatDateTime,
  formatTime,
} from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { useAttributePreference } from "../common/util/preferences";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import { useSelector, useDispatch } from "react-redux";
import LogoImage from "../login/LogoImage";
import { nativePostMessage } from "../common/components/NativeInterface";
import { sessionActions } from "../store";

const columnsArray = [
  ["name", "sharedName"],
  ["uniqueId", "deviceIdentifier"],
  ["groupId", "groupDialog"],
  ["phone", "sharedPhone"],
  ["model", "deviceModel"],
  ["contact", "deviceContact"],
  ["category", "deviceCategory"],
  ["status", "deviceStatus"],
//   ["icc", "sharedIMEIChip"],
//   ["vendor", "sharedVendorChip"],
//   ["operator", "sharedOperatorChip"],
//   ["tech", "sharedTech"],
  ["lastUpdate", "deviceLastUpdate"],
  ["disabled", "sharedDisabled"],
];

const columnsMap = new Map(columnsArray);

// Componente para cabeçalho de impressão
const PrintHeader = ({ userNames, from, to }) => {
  const t = useTranslation();

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print();
                nativePostMessage(`print|${t('deviceTitle')}.pdf`);
              })
            );
          }}
          alt={t('companyLogo')}
        />
        <h1 style={{ fontSize: 24, margin: 0 }}>{t('deviceTitle')}</h1>
      </div>
      <div style={{ marginTop: 10, fontSize: 14, color: '#555' }}>
        {userNames && userNames.length > 0 && (
          <div>
            <strong>{t('sharedUser')}:</strong> {userNames.join(', ')}
          </div>
        )}
        {from && (
          <div>
            <strong>{t('reportFrom')}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong>{t('reportTo')}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const DevicesReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.items || {});
  const groups = useSelector((state) => state.groups.items || {});
  const printing = useSelector((state) => state.session.printing);

  const formatDateTimeAttribute = useAttributePreference(
    "formatDateTime",
    null
  );

  const [columns, setColumns] = usePersistedState("deviceColumns", [
    "name",
    "contact",
    "uniqueId",
    "category",
    "model",
    "lastUpdate",
    "disabled",
  ]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  const handleSubmit = useCatch(
    async ({ userIds, from, to, type, mail, headers }) => {
      // Salvar parâmetros para impressão
      setSelectedUserIds(userIds || []);
      setFromDate(from);
      setToDate(to);

      if (type === 'pdf') {
        // Ativar modo de impressão
        dispatch(sessionActions.setPrinting(true));
        return;
      }

      setLoading(true);
      try {
        const query = new URLSearchParams({ from, to, mail });

        // Verificar se userIds existe antes de usá-lo
        if (userIds) {
          if (Array.isArray(userIds)) {
            userIds.forEach(id => query.append("userId", id));
          } else {
            query.append("userId", userIds);
          }
        }

        const response = await fetch(`/api/reports/devices?${query.toString()}`, {
          headers,
        });

        if (response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType) {
            if (contentType === "application/json") {
              const data = await response.json();
              setItems(data || []);
            } else {
              window.location.assign(
                window.URL.createObjectURL(await response.blob())
              );
            }
          }
        } else {
          console.error("Error fetching devices:", await response.text());
          setItems([]);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  );

  const formatValue = (item, key) => {
    if (!item) return "";

    try {
      switch (key) {
        case "category":
          if (!item[key]) return "";
          return t(`category${item[key].replace(/^\w/, (c) => c.toUpperCase())}`);
        case "disabled":
          return formatBoolean(item[key], t);
        case "lastUpdate":
          return item[key] ? formatTime(item[key], "DD/MM/YYYY HH:mm:ss") : null;
        case "groupId":
          return item[key] && groups[item[key]] ? groups[item[key]].name : "";
        default:
          return item[key] || "";
      }
    } catch (error) {
      console.error(`Error formatting value for key ${key}:`, error);
      return "";
    }
  };

  // Layout para modo de impressão
  if (printing) {
    const userNames = selectedUserIds.map(id => users[id]?.name || id).filter(Boolean);

    return (
      <div>
        <PrintHeader
          userNames={userNames}
          from={fromDate}
          to={toDate}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item) => (
                  <TableRow key={`${item.id || 'id'}_${item.uniqueId || ''}`}>
                    {columns.map((key) => (
                      <TableCell key={key}>{formatValue(item, key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow style={{ height: 72 }}>
                  <TableCell align="center" colSpan={columns.length}>
                    {t("sharedNoData") || "No data available"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Conteúdo da página
  const pageContent = (
    <>
      <div className={classes.header}>
        <ReportFilter
          handleSubmit={handleSubmit}
          includeUsers
          ignoreDevice
          ignorePeriod
          handleSchedule={undefined}
          showOnly={undefined}
          multiDevice={undefined}
          includeGroups={undefined}
          loading={loading}
        >
          <Grid item xs={12} sm={12}>
            <ColumnSelect
              columns={columns}
              setColumns={setColumns}
              columnsArray={columnsArray}
              rawValues={true}
              disabled={loading}
            />
          </Grid>
        </ReportFilter>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.length > 0 ? (
              items.map((item) => (
                <TableRow key={`${item.id || 'id'}_${item.uniqueId || ''}`}>
                  {columns.map((key) => (
                    <TableCell key={key}>{formatValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow style={{ height: 72 * 5 }}>
                <TableCell align="center" colSpan={columns.length}>
                  {t("sharedNoData") || "No data available"}
                </TableCell>
              </TableRow>
            )
          ) : (
            <TableShimmer 
              columns={columns.length} 
              startAction={false}
              endAction={false}
            />
          )}
        </TableBody>
      </Table>
    </>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "deviceTitle"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default DevicesReportPage;